import { VoiceAI } from './client';
import { makeRequest } from './utils/request';

/**
 * Warmup utilities for pre-warming models to avoid cold start delays
 */
export class Warmup {
  /**
   * Pre-warm a TTS model to avoid cold start delays
   * @param model - The TTS model to warm up (e.g., 'orpheus', 'elevenlabs/multi-v2')
   * @returns Promise that resolves when the model is ready
   */
  static async tts(model: string): Promise<void> {
    const client = VoiceAI.getInstance();
    const config = client.getConfig();
    
    try {
      // Send a minimal request to wake the model
      await makeRequest({
        url: `/v1/tts/${model.replace('/', '/')}`,
        method: 'POST',
        data: { 
          text: 'Test', 
          warmup: true  // Signal this is a warmup request
        },
        config,
        modelTimeout: 120000, // 2 minutes for warmup
        responseType: 'json',
      });
      
      console.log(`✓ Model ${model} is warmed up and ready`);
    } catch (error: any) {
      // If it's just a warmup, we can ignore some errors
      if (error.message.includes('timed out')) {
        console.log(`⚠ Model ${model} is warming up (may take 60-90s for first call)`);
      } else {
        throw error;
      }
    }
  }

  /**
   * Pre-warm an STT model
   * @param model - The STT model to warm up (e.g., 'whisper-v3')
   */
  static async stt(model: string): Promise<void> {
    const client = VoiceAI.getInstance();
    const config = client.getConfig();
    
    try {
      // Create minimal audio data (1 second of silence)
      const sampleRate = 16000;
      const duration = 1;
      const audioData = new Float32Array(sampleRate * duration);
      
      await makeRequest({
        url: `/v1/stt/${model}`,
        method: 'POST',
        data: {
          audio: Buffer.from(audioData.buffer).toString('base64'),
          warmup: true
        },
        config,
        modelTimeout: 120000,
        responseType: 'json',
      });
      
      console.log(`✓ Model ${model} is warmed up and ready`);
    } catch (error: any) {
      if (error.message.includes('timed out')) {
        console.log(`⚠ Model ${model} is warming up (may take 60-90s for first call)`);
      } else {
        // Ignore warmup-specific errors
        console.log(`✓ Model ${model} warmup attempted`);
      }
    }
  }

  /**
   * Pre-warm an LLM model
   * @param model - The LLM model to warm up (e.g., 'llama-4-scout')
   */
  static async llm(model: string): Promise<void> {
    const client = VoiceAI.getInstance();
    const config = client.getConfig();
    
    try {
      await makeRequest({
        url: `/v1/llm/${model}`,
        method: 'POST',
        data: {
          messages: [{ role: 'user', content: 'Hi' }],
          max_tokens: 1,
          warmup: true
        },
        config,
        modelTimeout: 120000,
        responseType: 'json',
      });
      
      console.log(`✓ Model ${model} is warmed up and ready`);
    } catch (error: any) {
      if (error.message.includes('timed out')) {
        console.log(`⚠ Model ${model} is warming up (may take 60-90s for first call)`);
      } else {
        console.log(`✓ Model ${model} warmup attempted`);
      }
    }
  }

  /**
   * Pre-warm multiple models in parallel
   * @param models - Array of model identifiers with their types
   * @example
   * await Warmup.multiple([
   *   { type: 'tts', model: 'orpheus' },
   *   { type: 'stt', model: 'whisper-v3' },
   *   { type: 'llm', model: 'llama-4-scout' }
   * ]);
   */
  static async multiple(models: Array<{ type: 'tts' | 'stt' | 'llm', model: string }>): Promise<void> {
    console.log(`Warming up ${models.length} models...`);
    
    const promises = models.map(({ type, model }) => {
      switch (type) {
        case 'tts':
          return this.tts(model);
        case 'stt':
          return this.stt(model);
        case 'llm':
          return this.llm(model);
        default:
          return Promise.resolve();
      }
    });
    
    await Promise.allSettled(promises);
    console.log('✓ All models warmed up');
  }
}

export const warmup = Warmup;